import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type Category,
  type CreateCategoryInput,
  type CreateTransactionInput,
  type ListTransactionsFilters,
  type Transaction,
  type TransactionDirection,
  type TransactionsList,
  type UpdateCategoryInput,
  type UpdateTransactionInput,
  transactionsApi,
} from "../lib/transactions";

const baseKey = ["transactions"] as const;
const categoriesKey = ["categories"] as const;

export function useCategories(direction?: TransactionDirection) {
  return useQuery<{ categories: Category[] }>({
    queryKey: [...categoriesKey, direction ?? "all"],
    queryFn: () => transactionsApi.listCategories(direction),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCategoryInput) => transactionsApi.createCategory(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: categoriesKey }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateCategoryInput }) =>
      transactionsApi.updateCategory(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: categoriesKey });
      qc.invalidateQueries({ queryKey: baseKey });
    },
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transactionsApi.deleteCategory(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: categoriesKey });
      qc.invalidateQueries({ queryKey: baseKey });
    },
  });
}

export function useTransactions(filters: ListTransactionsFilters = {}) {
  return useQuery<TransactionsList>({
    queryKey: [...baseKey, "list", filters],
    queryFn: () => transactionsApi.listTransactions(filters),
    staleTime: 30_000,
  });
}

export function useTransaction(id: string) {
  return useQuery<Transaction>({
    queryKey: [...baseKey, "detail", id],
    queryFn: () => transactionsApi.getTransaction(id),
    enabled: Boolean(id),
  });
}

export function useCreateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTransactionInput) => transactionsApi.createTransaction(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: baseKey }),
  });
}

export function useUpdateTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTransactionInput }) =>
      transactionsApi.updateTransaction(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: categoriesKey });
    },
  });
}

export function useDeleteTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => transactionsApi.deleteTransaction(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: baseKey }),
  });
}

export function useBulkCategorize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      transactionIds,
      categoryId,
    }: {
      transactionIds: string[];
      categoryId: string | null;
    }) => transactionsApi.bulkCategorize(transactionIds, categoryId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: baseKey });
      qc.invalidateQueries({ queryKey: categoriesKey });
    },
  });
}
